import { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import type { ComputedSlot } from '@vagusx/clients';
import { SlotSearchForm } from '../components/SlotSearchForm';
import { SlotResults } from '../components/SlotResults';
import { SchedulingChat } from '../components/SchedulingChat';
import { BookingDialog } from '../components/BookingDialog';
import { useSlotSearch } from '../hooks/use-slot-search';
import { useBooking } from '../hooks/use-booking';
import { cn } from '@/lib/utils';
import type { SlotSearchParams } from '../types';

export default function FindSlotsPage(): React.ReactElement {
  const [mode, setMode] = useState<'form' | 'chat'>('form');
  const [chatContext, setChatContext] = useState<SlotSearchParams | null>(null);
  const [bookingSlot, setBookingSlot] = useState<ComputedSlot | null>(null);

  const { slots, computationMs, isSearching, error, search, updateSlots, refresh, removeSlot } =
    useSlotSearch();
  const { isBooking, book } = useBooking();

  const handleSearch = (params: SlotSearchParams): void => {
    search(params);
  };

  const handleSwitchToChat = (params: SlotSearchParams): void => {
    setChatContext(params);
    setMode('chat');
  };

  const handleSwitchToForm = (): void => {
    setMode('form');
  };

  const handleBook = (slot: ComputedSlot): void => {
    setBookingSlot(slot);
  };

  const navigate = useNavigate();
  const goBack = (): void => {
    navigate({ to: '/appointments' });
  };

  // No Escape-to-leave here: this page holds the user's filters and results, and
  // Escape pressed on a focused field (a picked type, a picker trigger, a date)
  // reached the page and threw them away. Cancel and the back arrow leave.

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-4 border-b shrink-0">
        <Link to="/appointments">
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <h1 className="text-xl font-semibold">Find Available Slots</h1>
        <div className="flex-1" />
        <Button variant="outline" onClick={goBack}>
          Cancel
        </Button>
      </div>

      {/* Content — always 2-column grid, left column switches between form and chat.
          In chat mode side by side, the chat fills the visible height, so its
          input stays on screen, and the results scroll on their own. Stacked,
          the chat gets a fixed height and the page scrolls. */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className={cn('max-w-6xl mx-auto p-6', mode === 'chat' && 'lg:h-full')}>
          <div className={cn('flex flex-col gap-8 lg:flex-row', mode === 'chat' && 'lg:h-full')}>
            {/* Left column: Form or Chat */}
            <div className={cn('min-h-0 lg:w-96 lg:shrink-0', mode === 'chat' && 'h-96 lg:h-full')}>
              {mode === 'form' ? (
                <div>
                  <SlotSearchForm
                    onSearch={handleSearch}
                    onSwitchToChat={handleSwitchToChat}
                    isSearching={isSearching}
                    showAppointmentType={true}
                  />
                  {error && <p className="text-sm text-destructive mt-2">{error}</p>}
                </div>
              ) : (
                <div className="h-full">
                  <SchedulingChat
                    initialContext={chatContext!}
                    onSwitchToForm={handleSwitchToForm}
                    showBookButton={true}
                    onBook={handleBook}
                    onSlotsFound={updateSlots}
                  />
                </div>
              )}
            </div>

            {/* Right column: Results (always visible) */}
            <div className={cn('min-w-0 flex-1', mode === 'chat' && 'lg:overflow-y-auto')}>
              <SlotResults
                slots={slots}
                isLoading={isSearching}
                showBookButton={true}
                onBook={handleBook}
                computationMs={computationMs}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Booking Dialog */}
      <BookingDialog
        open={bookingSlot !== null}
        slot={bookingSlot}
        onClose={() => setBookingSlot(null)}
        onBook={async (data) => {
          const success = await book(data);
          if (success) {
            setBookingSlot(null);
            removeSlot(data.slot);
          }
          // The results are a snapshot: re-run the search so a booked time, or one
          // the EHR refused, is no longer offered.
          void refresh();
        }}
        isBooking={isBooking}
        canSearchPatients={true}
      />
    </div>
  );
}
